import { inject } from '@angular/core';

import { ActiveCoverageService, Package } from '@lib/insurance-packages/data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, catchError, concat, filter, map, mergeMap, of, switchMap, take } from 'rxjs';

import { VisitStatusTypes } from '@lib/core/models/visit.interface';

import { MemberHubService } from '../services/member-hub.service';
import * as MemberHubActions from './member-hub.actions';
import { selectMemberPackage } from './member-hub.selectors';

export const fetchMemberProfileEffect = createEffect(
  (actions$ = inject(Actions), memberHubService = inject(MemberHubService)): Observable<Action> =>
    actions$.pipe(
      ofType(MemberHubActions.fetchMemberProfile),
      switchMap(action =>
        memberHubService.getMemberProfile(action.memberId).pipe(
          map(memberProfile => MemberHubActions.fetchMemberProfileSuccess({ memberProfile })),
          catchError(() => of(MemberHubActions.fetchMemberProfileFailure({ error: 'Failed to fetch member profile' })))
        )
      )
    ),
  { functional: true }
);

export const fetchMemberPackageEffect = createEffect(
  (actions$ = inject(Actions), activeCoverageService = inject(ActiveCoverageService)): Observable<Action> =>
    actions$.pipe(
      ofType(MemberHubActions.fetchMemberPackage),
      switchMap(({ userId }) =>
        activeCoverageService.getUserPackage(userId).pipe(
          map(memberPackage => MemberHubActions.fetchMemberPackageSuccess({ userId, memberPackage })),
          catchError(() => of(MemberHubActions.fetchMemberPackageFailure({ userId, error: 'Failed to fetch member package' })))
        )
      )
    ),
  { functional: true }
);

export const fetchMemberAppointmentsEffect = createEffect(
  (actions$ = inject(Actions), memberHubService = inject(MemberHubService)): Observable<Action> =>
    actions$.pipe(
      ofType(MemberHubActions.fetchMemberAppointments),
      switchMap(action =>
        memberHubService
          .searchVisits({
            userId: action.userId,
            page: 1,
            pageSize: 20,
            status: VisitStatusTypes.Done,
            sortBy: 'creationDate',
          })
          .pipe(
            map(response => MemberHubActions.fetchMemberAppointmentsSuccess({ appointments: response.value || [] })),
            catchError(() => of(MemberHubActions.fetchMemberAppointmentsFailure({ error: 'Failed to fetch member appointments' })))
          )
      )
    ),
  { functional: true }
);

export const fetchMemberActiveCoverageEffect = createEffect(
  (actions$ = inject(Actions), store = inject(Store), activeCoverageService = inject(ActiveCoverageService)): Observable<Action> =>
    actions$.pipe(
      ofType(MemberHubActions.fetchMemberActiveCoverage),
      switchMap(({ userId }) => {
        const coverageFailure = () => MemberHubActions.fetchMemberActiveCoverageFailure({ userId, error: 'Failed to fetch member active coverage' });
        const loadCoverage = (memberPackage: Package | null): Observable<Action> =>
          (memberPackage?.id ? activeCoverageService.getActiveCoverage(memberPackage.id) : of(null)).pipe(
            map(coverage => MemberHubActions.fetchMemberActiveCoverageSuccess({ userId, coverage })),
            catchError(() => of(coverageFailure()))
          );
        const packageSettled$ = actions$.pipe(
          ofType(MemberHubActions.fetchMemberPackageSuccess, MemberHubActions.fetchMemberPackageFailure),
          filter(action => action.userId === userId),
          take(1),
          switchMap(action => ('memberPackage' in action ? loadCoverage(action.memberPackage) : of(coverageFailure())))
        );

        return store.select(selectMemberPackage).pipe(
          take(1),
          switchMap(memberPackage => {
            if (memberPackage?.userId !== userId || memberPackage.error) return concat(of(MemberHubActions.fetchMemberPackage({ userId })), packageSettled$);
            return memberPackage.loading ? packageSettled$ : loadCoverage(memberPackage.data);
          })
        );
      })
    ),
  { functional: true }
);

export const fetchMemberCoverageOptionDiagnosesEffect = createEffect(
  (actions$ = inject(Actions), activeCoverageService = inject(ActiveCoverageService)): Observable<Action> =>
    actions$.pipe(
      ofType(MemberHubActions.fetchMemberCoverageOptionDiagnoses),
      mergeMap(({ coverageId, optionId }) =>
        activeCoverageService.getOptionDiagnoses(coverageId, optionId).pipe(
          map(diagnoses => MemberHubActions.fetchMemberCoverageOptionDiagnosesSuccess({ optionId, diagnoses })),
          catchError(() => of(MemberHubActions.fetchMemberCoverageOptionDiagnosesFailure({ optionId, error: 'Failed to fetch coverage option diagnoses' })))
        )
      )
    ),
  { functional: true }
);
